import { useEffect, useMemo } from 'react';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { useSSRPreviewMode } from './bff-api/customers/useSSRPreviewMode';
import { EnhancedError } from './components/_error/EnhancedError';
import { LoadingIndicator } from './components/loading-indicator/LoadingIndicator';
import { Redirect } from './components/redirect/Redirect';
import { detectStorefrontPreview } from './utils/preview-mode-utils';
import { isHydrated, isServer } from './utils/utils';

export const LoadFromServer: PageFC = () => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const { req } = useServerContext();
    const isInPreviewMode = useMemo(
        () => isSSRPreviewMode || detectStorefrontPreview(),
        [isSSRPreviewMode], // detectStorefrontPreview is not a dependency because it is a pure function that always returns the same value for a given session
    );
    useEffect(() => {
        // no redirection for the preview mode since there is no SG here.
        // Assuming isInPreviewMode cannot change from false to true,
        // since after we are in preview mode, we are not going to be able to leave it without reloading the page.
        if (isHydrated() && !isInPreviewMode) {
            if (window.location.href.match(/https?:\/\/localhost:3000/)) {
                window.location.assign(window.location.href.replace(/3000/, '8000'));
            } else {
                window.location.reload();
            }
        }
    }, [isInPreviewMode]);

    const fullUrl = useMemo(
        () =>
            isServer && req
                ? `${req.protocol}://${req.get('host')}${req.originalUrl}`
                : window.location.href,
        [req],
    );

    // no redirection nor throwing an error for the preview mode, just show the error page
    if (isInPreviewMode) {
        return <EnhancedError error={new Error('Page preview - SFCC redirect disabled')} />;
    }

    if (isServer && req.url.match(/^\/s\//)) {
        // What to do with /s/* URLs?
        // This is an error scenario because this URL is not valid for PWA
        console.warn('PWA is not able to render the page, forcing the ups error page');
        throw new Error('PWA is not able to render the page');
    }

    if (isServer && !fullUrl.match(/^https?:\/\/localhost/)) {
        // One LoadFromServer in the server side means:
        // PWA is not able to render the page, so we need to redirect to the SFCC page
        // To avoid infinite redirects, we need to /s/SLT/
        console.warn('Forcing redirect to SFCC, since PWA is not able to render the page');
        return <Redirect to={`/s/SLT${req.url}`} type="sg" status={302} />;
    }
    return <LoadingIndicator isLoading />;
};
